import type { ReactNode } from "react";

type Tone = "primary" | "accent" | "secondary" | "neutral";

const TONES: Record<Tone, { bubble: string; ring: string }> = {
  primary: { bubble: "bg-primary-500/25 text-primary-200", ring: "border-accent-500/25" },
  accent: { bubble: "bg-accent-500/20 text-accent-200", ring: "border-accent-500/35" },
  secondary: { bubble: "bg-secondary-500/25 text-secondary-200", ring: "border-secondary-500/30" },
  neutral: { bubble: "bg-background-300/40 text-foreground-700", ring: "border-background-300/40" },
};

interface KpiCardProps {
  icon: string;
  label: string;
  value: string;
  hint?: string;
  tone?: Tone;
  progress?: number;
  badge?: string;
  children?: ReactNode;
}

const KpiCard = ({
  icon,
  label,
  value,
  hint,
  tone = "primary",
  progress,
  badge,
  children,
}: KpiCardProps) => {
  const t = TONES[tone];
  return (
    <div className={`glass-panel rounded-lg p-4 md:p-5 border ${t.ring} animate-fade-up`}>
      <div className="flex items-start justify-between gap-3">
        <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${t.bubble}`}>
          <i className={`${icon} text-xl`} />
        </span>
        {badge && (
          <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-accent-500/20 text-accent-200 whitespace-nowrap">
            {badge}
          </span>
        )}
      </div>
      <p className="text-[11px] md:text-xs font-medium text-foreground-500 mt-3">{label}</p>
      <p className="text-lg md:text-2xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
        {value}
      </p>
      {typeof progress === "number" && (
        <div className="mt-3 h-1.5 rounded-full bg-background-300/40 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-400 to-accent-500 transition-all duration-700"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      )}
      {hint && <p className="text-[11px] text-foreground-500 mt-2">{hint}</p>}
      {children}
    </div>
  );
};

export default KpiCard;